import { useEffect, useState } from 'preact/hooks';
import { listAllRecordsByType, type RecordRow } from '../lib/api';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './Registry.css';
import './HouseholdStats.css';

// A3 — THE STATS TAB. The one tab in the pack's rail that is not a list.
//
// WHAT A PARENT ACTUALLY ASKS. Not "how many auditions has she done" — the
// number on its own is a scoreboard, and a scoreboard about a nine-year-old's
// rejections is the wrong instrument entirely. The useful question is WHERE
// THE WORK IS SITTING: how much is out and unanswered, how much came back,
// how much became a booking. That is a shape, and the lifecycle already
// holds it.
//
// So this screen is the pack's own lifecycle, counted. Every bar is a real
// state from recordTypes.ts — never a bucket this file invented — which is
// also why nothing here needs updating when a stage is added: an unfamiliar
// state renders as itself rather than being dropped into "other".
//
// NO RATES, NO PERCENTAGES, NO TRENDS. A booking rate computed over a
// household with eleven auditions is noise presented as insight, and a
// trend line over a child's career is the exact thing this product must not
// become. Counts, in the lifecycle's own words.
//
// B20b §5 — SUBMISSIONS ARE COUNTED HERE (the CallToWrap demo walk,
// 2026-09-27: the counting had been built on the retired actor pack's
// /api/actor/stats, which only the legacy `actors` pack registers — no
// CallToWrap screen could reach it). Same rule as the other two blocks: the
// pack's own lifecycle states, counted, in its own words. The spec's
// "invited rate" is NOT drawn — see NO RATES above; that contradiction is
// Huan's to rule on, not this file's to settle.
//
// 2026-09-19 — READ TENANT-WIDE. This screen used to read through
// lib/subjectScope.ts, now gone: an account is one career, so there is
// nothing left to narrow into and this reads the tenant's own records, full
// stop.

export interface HouseholdStatsProps {
  tenantId: string;
}

type LoadState = 'loading' | 'ready' | 'error';

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Counts by lifecycle state, in first-seen order.
 *
 * A record with no lifecycle state is counted under its own honest label
 * rather than silently dropped: a row that exists and appears in no bar is a
 * total that does not add up, which is worse than an untidy chart. */
function tallyByState(rows: RecordRow[]): { state: string; label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = row.lifecycle_state ?? '';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()].map(([state, count]) => ({
    state,
    label: state === '' ? 'No state recorded' : humanize(state),
    count,
  }));
}

export function HouseholdStats({ tenantId }: HouseholdStatsProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [auditions, setAuditions] = useState<RecordRow[]>([]);
  const [engagements, setEngagements] = useState<RecordRow[]>([]);
  const [submissions, setSubmissions] = useState<RecordRow[]>([]);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setState('loading');
      setFailure(null);
      try {
        // Paged to the whole truth, for a sharper reason than the lists
        // have: a chart built on a first page is not a short chart, it is a
        // chart of a different household, and it looks exactly as convincing
        // as the real one.
        const [a, e, s] = await Promise.all([
          listAllRecordsByType(tenantId, 'aa_audition'),
          listAllRecordsByType(tenantId, 'aa_engagement'),
          listAllRecordsByType(tenantId, 'aa_submission'),
        ]);
        if (cancelled) return;
        setAuditions(activeOnly(a));
        setEngagements(activeOnly(e));
        setSubmissions(activeOnly(s));
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setState('error');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce]);

  if (state === 'loading') {
    return (
      <div class="registry">
        <div class="registry__eyebrow">Stats</div>
        <div class="registry__say">One moment…</div>
      </div>
    );
  }

  if (state === 'error' && failure !== null) {
    return (
      <div class="registry">
        <div class="registry__eyebrow">Stats</div>
        <div class="registry__say">
          {failure.headline}
          <br />
          <span class="registry__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="registry__more" onClick={() => setRetryNonce((n) => n + 1)}>
            Read this again
          </button>
        ) : null}
      </div>
    );
  }

  const blocks = [
    { key: 'submissions', label: 'Submissions', rows: tallyByState(submissions), empty: 'Nothing submitted yet.' },
    { key: 'auditions', label: 'Auditions', rows: tallyByState(auditions), empty: 'Nothing out yet.' },
    { key: 'bookings', label: 'Bookings', rows: tallyByState(engagements), empty: 'No bookings yet.' },
  ];

  return (
    <div class="registry">
      <div class="registry__eyebrow">Stats</div>
      {blocks.map((block) => {
        const max = Math.max(...block.rows.map((r) => r.count), 1);
        return (
          <div key={block.key}>
            <div class="registry__sec">
              <span>{block.label}</span>
              {block.rows.length > 0 ? (
                <span class="registry__mini">
                  {block.rows.reduce((sum, r) => sum + r.count, 0)}
                </span>
              ) : null}
            </div>
            {block.rows.length === 0 ? (
              <div class="registry__empty">{block.empty}</div>
            ) : (
              block.rows.map((row) => (
                <div class="hstats__row" key={row.state}>
                  <span class="hstats__label">{row.label}</span>
                  <span class="hstats__bar">
                    <span
                      class="hstats__fill"
                      style={{ width: `${Math.round((row.count / max) * 100)}%` }}
                    />
                  </span>
                  <span class="hstats__count">{row.count}</span>
                </div>
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}
